import { useEffect } from 'react';

import initShence from '@/hooks/useShence';
import UserStore, { CurrentVip } from '@/store/User';
import { APP_VERSION } from '@/version';

import { formatDate, getCookie } from '..';
import { logFirebaseEvent } from '../firebaseAnalytics';
import { AIFunction, FC_Feature, ROUTE_FROM } from './../const';
import {
  EventNewID,
  EventNewIDParams,
  GoogleEventID,
  PageVisitFrom,
  TLoginInType,
} from './eventNewConfig';
import {
  getGTMAppName,
  getSubscriptionPlan,
  getSubscriptionStatus,
  pushGTMEvent,
} from './gtmDataLayer';

const TID = 'UA-FalcoCut-Web';
const PID = 2;

const isPaid = () => {
  const { currentVip } = UserStore.getState();
  const { isExpireUser, key } = currentVip;
  return isExpireUser ? 2 : key === 'freeVip' ? 0 : 1;
};

// 神策平台只支持function写法 —— begin
function vipType() {
  const { currentVip } = UserStore.getState();
  const { key, startTime, endTime } = currentVip;

  const getDurationType = (start: number, end: number) => {
    const diff = end - start;
    const day = 24 * 60 * 60 * 1000;
    if (diff < 32 * day) return '1'; // 月订阅
    if (diff > 360 * day) return '2'; // 年订阅
    return '1';
  };

  if (key === 'enterpriseVip') {
    return 'E';
  }
  if (key === 'advancedVip') {
    return `P${getDurationType(startTime, endTime)}`;
  }
  if (key === 'basicVip') {
    return `S${getDurationType(startTime, endTime)}`;
  }

  return currentVip.key;
}

const getUID = () => {
  const user = getCookie('user');
  return user?.uid || '';
};

const getResolution = () => `${window.screen.width}*${window.screen.height}`;

const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
// 神策平台只支持function写法 —— end

// Tracker 实例缓存
let trackerInstance: any = null;

/**
 * 获取动态公共参数
 * 包含：用户ID、浏览器信息、VIP身份、版本号等
 */
const getDynamicPublicParams = () => {
  return {
    is_paid: isPaid,
    vip_type: vipType,
    tid: TID,
    pver: APP_VERSION,
    pid: PID,
    browser_lang: navigator.language,
    screen_resolution: getResolution,
    timezone: getTimezone,
    environment: process.env.NEXT_PUBLIC_ENV,
    last_visit_time: function () {
      return formatDate(Date.now());
    },
  };
};

const getUserInfoParams = (params?: CurrentVip) => {
  const currentVip = params || UserStore.getState().currentVip;
  const { userPointDetails, userRegisterTime } = currentVip;

  return {
    uid: getUID(),
    is_paid: isPaid(),
    vip_type: vipType(),
    tid: TID,
    pver: APP_VERSION,
    pid: PID,
    register_date: formatDate(userRegisterTime),
    timezone: getTimezone(),
    browser_lang: navigator.language,
    screen_resolution: getResolution(),
    total_consumed_credits: userPointDetails?.reduce(
      (sum, item) => sum + (item.used || 0),
      0,
    ),
  };
};

/**
 * 初始化 Tracker
 * 保证单例，避免重复初始化
 */
export const initTracker = async () => {
  if (typeof window === 'undefined') return null;
  if (trackerInstance) return trackerInstance;

  try {
    trackerInstance = await initShence();

    // 初始化成功后，设置初始公共属性和登录状态
    if (trackerInstance) {
      const params = getDynamicPublicParams();
      trackerInstance.registerPage(params);
      const uid = getCookie('user')?.uid;
      uid && trackerInstance.login(uid);
    }
  } catch (error) {
    console.error('Init tracker failed:', error);
  }
  return trackerInstance;
};

/**
 * 1. 自定义事件上报 (Track Event)
 * @param event 事件名称
 * @param params 事件属性
 */
export const trackEvent = async <T extends EventNewID>(
  event: T,
  params?: T extends keyof EventNewIDParams ? EventNewIDParams[T] : Record<string, any>,
) => {
  try {
    const tracker = await initTracker();
    if (!tracker) return;
    console.log(event, '====', params);
    tracker.track(event, params);
  } catch (error) {
    console.error(`Track event "${event}" failed:`, error);
  }
};

/**
 * 3. 用户表属性上报 (Set User Profile)
 * 设置用户的固有属性（如年龄、性别、注册时间等）
 * @param params 用户属性对象
 */
export const setUserProfile = async (params: CurrentVip) => {
  try {
    const tracker = await initTracker();
    tracker?.setProfile(getUserInfoParams(params));
  } catch (error) {
    console.error('Set user profile failed:', error);
  }
};

/**
 * 直接设置用户属性（透传 setProfile）
 */
export const setUserProfileDirect = async (profile: Record<string, any>) => {
  try {
    const tracker = await initTracker();
    tracker?.setProfile(profile);
  } catch (error) {
    console.error('Set user profile direct failed:', error);
  }
};

/**
 * 4. 用户登录 (User Login)
 * 关联用户 ID
 * @param userId 用户 ID
 */
type UserLoginOptions = {
  userId?: string;
  type: TLoginInType;
};

export const userLogin = async (options: UserLoginOptions) => {
  try {
    const tracker = await initTracker();
    const uid = options?.userId || getCookie('user')?.uid;
    uid && tracker?.login(uid);
    // 登录后上报一次登录埋点，附带登录方式
    trackEvent(EventNewID.USER_LOGIN, {
      source: options.type,
    });
  } catch (error) {
    console.error('User login failed:', error);
  }
};

// 用户登出
export const userLogout = async () => {
  try {
    const tracker = await initTracker();
    tracker?.logout();
  } catch (error) {
    console.error('User logout failed:', error);
  }
};

/**
 * 错误上报
 */
export const postError = async (error: Error, detail?: any) => {
  if (typeof window === 'undefined') return;
  try {
    const { datafluxRum } = await import('@cloudcare/browser-rum');
    datafluxRum?.addError(error, detail);
  } catch (e) {
    console.error('Post error failed:', e);
  }
};

// 上报页面访问埋点
export const usePageVisitTrack = (
  feature: AIFunction,
  options?: { enable?: boolean },
) => {
  const { enable = true } = options || {};

  useEffect(() => {
    if (!enable) return;
    sessionStorage.setItem(FC_Feature, feature);
    const from = sessionStorage.getItem(ROUTE_FROM) || PageVisitFrom.FIRST_URL_LOAD;
    const [entry] = performance.getEntriesByType('navigation');
    const { duration } = entry;
    trackEvent(EventNewID.FEATURE_VISIT, {
      source: feature,
      from: from as PageVisitFrom,
      duration,
    });
    sessionStorage.removeItem(ROUTE_FROM);

    return () => {
      sessionStorage.removeItem(FC_Feature);
    };
  }, [feature]);

  // app_view 等会员信息就绪后再上报
  const isRequestEnd = UserStore((s) => s.currentVip.isRequestEnd);
  useEffect(() => {
    if (!enable) return;
    const app_name = getGTMAppName(feature);
    if (!getCookie('user')?.uid) {
      pushGTMEvent('app_view', {
        app_name,
      });
      return;
    }
    if (!isRequestEnd) return;
    pushGTMEvent('app_view', {
      app_name,
      subscription_plan: getSubscriptionPlan(),
      subscription_status: getSubscriptionStatus(),
    });
  }, [feature, isRequestEnd]);
};

export const trackGoogleEvent = (
  eventName: GoogleEventID,
  params?: Record<string, any>,
) => {
  if (typeof window === 'undefined') return false;
  logFirebaseEvent(eventName, {
    user_id: getUID() || null,
    is_free_user: isPaid() === 0,
    ...params,
  }).catch((error) => {
    console.error('[analytics] logFirebaseEvent failed:', error);
  });
  return true;
};

// 上报首次完成核心动作埋点
export const trackFirstCoreAction = (feature: AIFunction) => {
  // 增加localStorage标记，避免重复上报
  const flagKey = 'fc_action_tracked_' + feature;
  if (localStorage.getItem(flagKey)) return;
  trackGoogleEvent(GoogleEventID.FirstCoreAction, {
    feature,
  }) && localStorage.setItem(flagKey, '1');
};
